// ============================================================================
// SHARED FORMATTERS
//
// Common formatting helpers used across analytics dashboards.
// Ensures consistent display of time, percentages, and counts.
// ============================================================================

/**
 * Format minutes as human-readable time
 * 0 → null (for empty state handling)
 * < 60 min → "Xm"
 * ≥ 60 min → "Xh Ym"
 *
 * @param {number|null|undefined} minutes - Total minutes
 * @returns {string|null} - Formatted string or null for empty state
 */
export const formatMinutesHuman = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === 0) {
    return null;
  }

  const totalMins = Math.round(minutes);

  if (totalMins < 60) {
    return `${totalMins}m`;
  }

  const hours = Math.floor(totalMins / 60);
  const remainingMins = totalMins % 60;

  if (remainingMins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMins}m`;
};

/**
 * Format percentage consistently
 * - Shows one decimal place
 * - Returns '—' for empty/no-data states
 *
 * @param {number} value - Percentage value (0-100)
 * @param {boolean} hasData - Whether there is data to display
 * @returns {string} - Formatted percentage or placeholder
 */
export const formatPercent = (value, hasData = true) => {
  if (!hasData || value === null || value === undefined) {
    return '—';
  }
  return `${value.toFixed(1)}%`;
};

/**
 * Format count with proper empty state handling
 *
 * @param {number} value - Count value
 * @param {boolean} hasData - Whether there is data to display
 * @returns {string} - Formatted count or placeholder
 */
export const formatCount = (value, hasData = true) => {
  if (!hasData || value === null || value === undefined) {
    return '—';
  }
  return value.toString();
};

/**
 * Format "last updated" as relative time
 * < 1 min → "just now"
 * 1-59 min → "X minutes ago"
 * 1-23 hours → "X hours ago"
 * ≥ 24 hours → "X days ago"
 *
 * @param {Date|null} date - The date to format
 * @returns {string|null} - Relative time string or null
 */
export const formatLastUpdated = (date) => {
  if (!date) return null;

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
};

/**
 * Format date as YYYY-MM-DD (UTC)
 *
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDateId = (date) => {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
